<script setup lang="ts">
import PhosphorIcon from "./PhosphorIcon.vue";

withDefaults(
  defineProps<{
    icon?: string;
    phosphorIcon?: string;
    phosphorWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
    disabled?: boolean;
    variant?:
      | "primary"
      | "secondary"
      | "outline"
      | "outline-primary"
      | "outline-success"
      | "outline-danger"
      | "flat"
      | "flat-white"
      | "flat-primary"
      | "flat-success"
      | "flat-danger"
      | "ia";
    size?: number;
    loading?: boolean;
  }>(),
  {
    variant: "primary",
    size: 40,
    disabled: false,
    loading: false,
    phosphorWeight: "regular",
  }
);
</script>

<template>
  <button
    v-bind="$attrs"
    class="p-0"
    :class="{
      'unelevated rounded ': variant === 'secondary',
      'unelevated unelevated-primary rounded': variant === 'primary',
      'outline rounded': variant === 'outline',
      'outline outline-primary rounded': variant === 'outline-primary',
      'outline outline-danger rounded': variant === 'outline-danger',
      'outline outline-success rounded': variant === 'outline-success',
      'outline outline-ia  ': variant === 'ia',
      'flat rounded': variant === 'flat',
      'flat flat-white rounded': variant === 'flat-white',
      'flat flat-primary rounded': variant === 'flat-primary',
      'flat flat-danger rounded': variant === 'flat-danger',
      'flat flat-success rounded': variant === 'flat-success',
      'disabled-btn': disabled || loading,
    }"
    :disabled="disabled || loading"
    :disable="disabled || loading"
    :style="{
      height: `${size}px`,
      width: `${size}px`,
      borderRadius: `8px`,
    }"
  >
    <span
      v-if="loading"
      class="spinner-border spinner-border-sm"
      role="status"
    ></span>
    <PhosphorIcon
      v-else-if="phosphorIcon"
      :name="phosphorIcon"
      :size="size / 2"
      :weight="phosphorWeight"
    />
    <i
      v-else-if="icon || variant === 'ia'"
      :class="variant === 'ia' ? icon ?? 'bi-stars' : icon"
      :style="`font-size: ${size / 2}px`"
    ></i>
  </button>
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

.unelevated {
  background-color: $dark;
  color: white !important;
  transition: background-color 0.2s ease, color 0.2s ease;
  border: 0;
  &:hover {
    background-color: darken($dark, 10%);
  }
}

.unelevated-primary {
  background-color: $primary;
  &:hover {
    background-color: darken($primary, 10%);
  }
}

.outline {
  background-color: var(--bs-white);
  transition: background-color 0.2s ease, color 0.2s ease;
  color: var(--bs-dark);
  border: 2px solid rgba($dark, 0.1);
  &:hover {
    color: white;
    background-color: var(--bs-dark);
  }
}

.outline-primary {
  color: var(--bs-primary);
  border: 2px solid rgba($primary, 0.1);
  &:hover {
    background-color: var(--bs-primary);
  }
}

.outline-ia {
  color: $ia;
  border: 2px solid rgba($ia, 0.1);
  &:hover {
    background-color: $ia;
  }
}

.outline-danger {
  color: var(--bs-danger);
  border: 2px solid rgba($danger, 0.1);
  &:hover {
    background-color: var(--bs-danger);
  }
}

.outline-success {
  color: var(--bs-success);
  border: 2px solid rgba($success, 0.1);
  &:hover {
    background-color: var(--bs-success);
  }
}

.flat {
  background-color: transparent;
  border: none;
  transition: background-color 0.2s ease, color 0.2s ease;
  color: var(--bs-dark);
}

.flat:hover {
  color: white;
  background-color: var(--bs-dark);
}

.flat-white {
  color: white;
  &:hover {
    background-color: var(--bs-dark);
  }
}
.flat-primary {
  color: var(--bs-primary);
  &:hover {
    background-color: var(--bs-primary);
  }
}

.flat-danger {
  color: var(--bs-danger);
}

.flat-danger:hover {
  background-color: var(--bs-danger);
}
.flat-success {
  color: var(--bs-success);
}

.flat-success:hover {
  background-color: var(--bs-success);
}

.disabled-btn {
  cursor: not-allowed !important;
  pointer-events: none !important;
  &:hover {
    background-color: inherit !important;
  }
}
</style>
